// From http://www.redblobgames.com/
// Copyright 2013 Red Blob Games <redblobgames@gmail.com>
// License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>

/* global Draggable */

const ops = require('ndarray-ops');
const zeros = require('zeros');
const fft = require('ndarray-fft');
const complex = require('ndarray-complex');
const render = require('./render');

const CANVASSIZE = 256;
const TAU = 2 * Math.PI;

/* Return a <= x <= b */
function clamp(x, a, b) {
    if (a > b) throw "clamp(_, a, b) requires a <= b";
    if (x < a) x = a;
    if (x > b) x = b;
    return x;
}


// 2D FFT

function outputToCanvas2D(id, array, halfoffset) {
    halfoffset = !!halfoffset;
    if (array.shape[0] !== array.shape[1]) throw "need square arrays in outputToCanvas2D";

    // Get a temp buffer to work with (avoid allocating these again)
    if (!outputToCanvas2D.buffers) { outputToCanvas2D.buffers = []; }
    if (!outputToCanvas2D.buffers[array.shape[0]]) { outputToCanvas2D.buffers[array.shape[0]] = zeros(array.shape); }
    let scaled = outputToCanvas2D.buffers[array.shape[0]];

    // Rescale the array to 0-255
    let inf = ops.inf(array), sup = ops.sup(array);
    let mul = 255 / Math.max(1e-4, (sup - inf)), add = -inf;
    ops.adds(scaled, array, add);
    ops.mulseq(scaled, mul);

    // Draw each point as a pixelsize X pixelsize box
    let canvas = document.getElementById(id);
    let pixelsize = Math.floor(CANVASSIZE / array.shape[0]);
    canvas.width = array.shape[0] * pixelsize;
    canvas.height = array.shape[1] * pixelsize;

    let context = canvas.getContext('2d');
    let pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    let k = 0, W = array.shape[0], H = array.shape[1];
    let i_offset = halfoffset*W/2, j_offset = halfoffset*H/2;
    for (let j = 0; j < H; j++) {
        for (let jj = 0; jj < pixelsize; jj++) {
            for (let i = 0; i < W; i++) {
                let y = scaled.get((i + i_offset) % W,
                                   (j + j_offset) % H);
                for (let ii = 0; ii < pixelsize; ii++) {
                    pixels.data[k++] = y;
                    pixels.data[k++] = y;
                    pixels.data[k++] = y;
                    pixels.data[k++] = 255;
                }
            }
        }
    }
    context.putImageData(pixels, 0, 0);
}


function TestPart() {
    let N = 64;
    let p = ops.random(zeros([N, N]));
    let f = zeros(p.shape);

    for (let x = 0; x < N; x++) {
        for (let y = 0; y < N; y++) {
            p.set(x, y, Math.cos((5*x/N + 3*y/N)*Math.PI*2));
        }
    }

    outputToCanvas2D('data-r', p);
    outputToCanvas2D('data-i', f);

    fft(1, p, f);  // seems to take 30-40 ms or so for 40x40 but only 10-20 for 64x64

    for (let x = 0; x < N; x++) {
        for (let y = 0; y < N; y++) {
            let x0 = x > (N >> 1) ? (N - x) : x;
            let y0 = y > (N >> 1) ? (N - y) : y;
            x0 = x; y0 = y;
            p.set(x, y, p.get(x0, y0));
            f.set(x, y, f.get(x0, y0));
        }
    }

    outputToCanvas2D('fft-r', p, true);
    outputToCanvas2D('fft-i', f, true);

    let mag = zeros(p.shape);
    let arg = zeros(p.shape);
    complex.abs(mag, p, f);
    complex.arg(arg, p, f);
    outputToCanvas2D('fft-m', mag, true);
    outputToCanvas2D('fft-a', arg, true);

    // NOTE: arg is pretty random at small magnitudes but mag is what we
    // really care about

    fft(-1, p, f);
    outputToCanvas2D('ifft-r', p);
    outputToCanvas2D('ifft-i', f);
}



class RandomValues {
    constructor(N) {
        this.N = N;
        this.x = zeros([N, N]);
        this.y = zeros([N, N]);
        this.generate();
    }

    generate() {
        let {N, x, y} = this;
        for (let i = 0; i < N; i++) {
            for (let j = 0; j < N; j++) {
                let r = Math.random() * N*N;
                let a = Math.random() * TAU;
                x.set(i, j, r * Math.cos(a));
                y.set(i, j, r * Math.sin(a));
            }
        }
    }
}
        
const RANDOM = new RandomValues(128);

class PartTwo {
    constructor () {
        this.rendering = false;
        this.N = 128;
        this.tr = zeros([this.N, this.N]);
        this.ti = zeros([this.N, this.N]);
        this.regen();
    }
    
    // 2D IFFT
    regen() {
        if (this.rendering) {
            // Avoid glitches from the slider moving quickly and then
            // we update the texture before rendering has finished.
            // This seems to happen in Chrome and Safari on Mac, seems
            // to be a race condition, but only while devtools is
            // open. But this *also* fixes keyboard control of the
            // slider.
            return;
        }
        this.rendering = true;
        let tr = this.tr, ti = this.ti;
        let Fmin = Number(document.getElementById("fmin").value);
        let Fmax = Fmin + Number(document.getElementById("frange").value);
        let exponent = Number(document.getElementById("exponent").value);
        document.getElementById("fmin-output").textContent = Fmin;
        document.getElementById("frange-output").textContent = Fmax - Fmin;
        document.getElementById("exponent-output").textContent = exponent.toFixed(1);
        for (let i = 0; i < this.N; i++) {
            for (let j = 0; j < this.N; j++) {
                if (i == 0 && j == 0) continue;
                let f1 = Math.min(i, this.N-i);
                let f2 = Math.min(j, this.N-j);
                let f = Math.sqrt(f1*f1+f2*f2);
                /* NOTE: why 2*exponent?? Eric Stansifer emailed me
                 * and said it's because the noise color is for the
                 * *power* spectrum and not the *amplitude*, and power
                 * is the square of the amplitude. */
                let scale = (Fmin <= f && f <= Fmax) * Math.pow(f, 2*exponent);
                let x = RANDOM.x.get(i, j) * scale;
                let y = RANDOM.y.get(i, j) * scale;
                tr.set(i, j, x);
                ti.set(i, j, y);
            }
        }

        outputToCanvas2D('2d-r', tr, true);
        fft(-1, tr, ti);
        outputToCanvas2D('ifft-2d-r', tr);

        requestAnimationFrame(() => {
            render.updateBuffers(tr); render.draw();
            this.rendering = false;
        });
    }
}


class PartThree {
    constructor() {
        this.freqs = zeros([128]);

        let N = 128;
        this.N = N;
        this.tr = zeros([N, N]);
        this.ti = zeros([N, N]);

        this.canvas = document.getElementById('freq-drawable');
        this.canvas.width = 256;
        this.canvas.height = 64;
        
        this.set_freq_by_exponent(-1);

        let diagram = this;
        new Draggable({
            el: this.canvas,
            drag(event) {
                const rect = this.el.getBoundingClientRect();
                let mouseX = event.x / rect.width * this.el.width;
                let mouseY = event.y / rect.height * this.el.height;

                let x1 = mouseX >> 1, y1 = clamp((64 - mouseY)/64, 0.0, 1.0);
                // note: special case, y1 set to -1.0 in log space if we're below
                // the drawing surface, to push freq very close to 0; I don't know
                // the best way to write a log-log frequency spectrum editor but
                // this hack seems to make it "good enough"
                if (mouseY > 64) y1 = -1.0;

                let x0 = x1, y0 = y1;
                if (this.mousedrag) {
                    x0 = this.mousedrag.x;
                    y0 = this.mousedrag.y;
                }

                if (x1 < 0 || x1 >= this.el.width) {
                    // Out of range -- stop any interpolations so when we return
                    // into range it starts a new stroke
                    delete this.mousedrag;
                    return;
                }

                let steps = Math.abs(x1 - x0);
                let dx = (x1 > x0)? 1 : -1;
                let dy = (y1 - y0) / steps;
                for (let i = 0; i < steps; i++) {
                    let x = x0 + i * dx, y = y0 + i * dy;
                    diagram.freqs.set(x, y);
                }
                diagram.freqs.set(x1, y1);

                this.mousedrag = {x: x1, y: y1};
                diagram.redraw();
                diagram.regen();
            }
        });
    }

    redraw() {
        let ctx = this.canvas.getContext('2d');
        ctx.clearRect(0, 0, 256, 64);
        ctx.fillStyle = "hsl(10, 30%, 50%)";
        ctx.beginPath();
        ctx.moveTo(0, 63);
        for (let x = 0; x < 128; x++) {
            let y = 63 * (1.0 - this.freqs.get(x));
            ctx.lineTo(2*x, y);
        }
        ctx.lineTo(255, 63);
        ctx.closePath();
        ctx.fill();
    };


    /* Interpolating lookup with a log-log scale */
    lookup_freq(array, x) {
        // The (nd)array is in log-log scale:
        //      array[log(x)] = log(y)
        // x should be between epsilon and array.length
        // y will be between epsilon and ??

        let len = array.shape[0];
        let i = len / Math.log(len) * Math.log(x);
        let i_floor = Math.floor(i);
        let i_frac = i - i_floor;
        let i0 = clamp(i_floor, 0, len-1);
        let i1 = clamp(i_floor+1, 0, len-1);
        let j0 = array.get(i0), j1 = array.get(i1);
        let j = j0 * (1.0 - i_frac) + j1 * i_frac;
        let y = Math.exp(j * Math.log(1000));
        return y;
    }

    /* Reassign frequencies to match a generator function */
    set_freq(array, f) {
        let len = array.shape[0], max = 1e-4;
        for (let i = 0; i < len; i++) {
            let x = Math.exp(Math.log(len) * i / len);
            let y = f(x);
            let j = Math.log(y);
            if (j > max) max = j;
            array.set(i, j);
        }
        for (let i = 0; i < len; i++) {
            array.set(i, array.get(i) / max);
        }
    }

    /* Reassign all frequencies to match an exponent */
    set_freq_by_exponent(p) {
        this.set_freq(this.freqs, function(x) { return 1000*Math.pow(x, 2*p); });
        this.redraw();
        this.regen();
    }

    regen() {
        let {tr, ti, N} = this;

        for (let i = 0; i < N; i++) {
            for (let j = 0; j < N; j++) {
                if (i == 0 && j == 0) {
                    tr.set(i, j, 0);
                    ti.set(i, j, 0);
                    continue;
                }
                let f1 = Math.min(i, N-i);
                let f2 = Math.min(j, N-j);
                let f = Math.sqrt(f1*f1+f2*f2);
                let scale = this.lookup_freq(this.freqs, f);
                let x = RANDOM.x.get(i, j) * scale;
                let y = RANDOM.y.get(i, j) * scale;
                tr.set(i, j, x);
                ti.set(i, j, y);
            }
        }

        fft(-1, tr, ti);
        outputToCanvas2D('freq-output', tr);
    }
}


// Main:
// let partOne = new TestPart();
window.RANDOM = RANDOM;
window.partTwo = new PartTwo();
window.partThree = new PartThree();
